import { create } from "zustand";
import { GameState } from "./types";
import { socket } from "./lib/socket";

function generateRoomCode() {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

let socketListenersInitialized = false;

/*
  initSocketListeners:
  - Attache les handlers socket UNE SEULE FOIS (évite la multiplication des listeners).
  - Les handlers ne font que mettre à jour le store; le serveur est la source de vérité.
  - Les handlers doivent être tolérants aux payloads partiels (utiliser ?? fallback).
*/
function initSocketListeners(set, get) {
  if (socketListenersInitialized) return;
  socketListenersInitialized = true;

  // roomCreated: confirmation serveur après createRoom.
  // Le code SERVEUR est prioritaire : il est la source de vérité. L'ancienne
  // version gardait le code client, donc si le serveur en avait choisi un autre
  // (collision, reclaim) le QR affichait un code qui ne correspondait à aucune room.
  socket.on("roomCreated", ({ code, players: srvPlayers, hostId } = {}) => {
    const finalCode = code || get().roomCode || null;
    set({
      isHost: true,
      roomCode: finalCode,
      roomError: null,
      roomHostId: hostId ?? get().roomHostId ?? null,
      // si srvPlayers absent, reconstruire un joueur localement (fallback)
      players: srvPlayers ?? [
        { id: socket.id, name: get().playerName, score: 0, position: 0 },
      ],
      loading: false,
      gameCompleted: false,
      finalPlayers: null,
    });
  });

  // roomJoined: confirmation après joinRoom / joinAsSpectator.
  socket.on(
    "roomJoined",
    ({ players, gameStarted, gameCompleted, hostId } = {}) => {
      set({
        roomCode: get().roomCode,
        roomHostId: hostId ?? get().roomHostId ?? null,
        players: players ?? [],
        gameStarted: !!gameStarted,
        gameCompleted: !!gameCompleted,
        // Les questions viennent du serveur via gameStarted, on ne les génère plus localement.
        loading: false,
      });
    }
  );

  // playerJoined / playerUpdated / playerLeft: mises à jour passives de la liste
  socket.on("playerJoined", ({ players, hostId } = {}) => {
    const isSpec = get().isSpectator;
    set({
      roomHostId: hostId ?? get().roomHostId ?? null,
      players: players ?? get().players,
      ...(isSpec ? { isSynced: true } : {}),
    });
  });
  socket.on("playerUpdated", ({ players, hostId } = {}) => {
    console.debug(
      "[socket] playerUpdated received",
      players?.map((p) => ({ id: p.id, pos: p.position, score: p.score }))
    );
    const isSpec = get().isSpectator;
    set({
      roomHostId: hostId ?? get().roomHostId ?? null,
      players: players ?? get().players,
      ...(isSpec ? { isSynced: true } : {}),
    });
  });
  socket.on("playerLeft", ({ players } = {}) =>
    set({ players: players ?? get().players })
  );

  // playerReady / allPlayersReady: synchronisation du flag ready
  socket.on("playerReady", ({ players } = {}) =>
    set({ players: players ?? get().players })
  );
  socket.on("allPlayersReady", ({ players, hostId } = {}) => {
    // Update players list and remember who is host; UI decides who reacts.
    set({
      players: players ?? get().players,
      roomHostId: hostId ?? get().roomHostId ?? null,
      loading: false,
    });
  });

  // gameStarted: les questions viennent du serveur (identiques pour tous les joueurs).
  socket.on(
    "gameStarted",
    ({ questions: serverQuestions, timePerQuestion } = {}) => {
    const isSpec = get().isSpectator;
    const t = typeof timePerQuestion === "number" && timePerQuestion > 0 ? timePerQuestion : 20;
    set({
      gameStarted: true,
      gameCompleted: false,
      finalPlayers: null,
      playerFinished: false,
      finishedPlayers: new Set(),
      questions: serverQuestions ?? [],
      currentQuestion: 0,
      score: 0,
      currentPosition: 0,
      timePerQuestion: t,
      timeRemaining: t,
      // Intro READY/SET/GO uniquement côté joueur (pas spectateur)
      introPhase: isSpec ? null : "ready",
      introStartedAt: isSpec ? null : Date.now(),
    });

    if (!isSpec) {
      // Phases : 0-2s READY, 2-4s SET, 4-6s GO!, puis question
      setTimeout(() => set({ introPhase: "set" }), 2000);
      setTimeout(() => set({ introPhase: "go" }), 4000);
      setTimeout(
        () => set({ introPhase: null, introStartedAt: null }),
        6000
      );
    }
  });

  // playerFinished: un joueur a fini ses questions (danse en attendant les autres)
  socket.on("playerFinished", ({ finishedPlayerIds } = {}) => {
    if (finishedPlayerIds) {
      set({ finishedPlayers: new Set(finishedPlayerIds) });
    }
  });

  // gameCompleted: TOUS les joueurs ont fini → afficher le leaderboard.
  // Le serveur joint le classement de l'arrivée : on le fige ici une bonne fois.
  // Fallback sur players si le payload manque (serveur plus ancien).
  socket.on("gameCompleted", ({ players: standings } = {}) => {
    set({
      gameCompleted: true,
      finalPlayers: standings?.length ? standings : get().players,
    });
  });

  // roomError: le code demandé n'existe pas côté serveur (QR périmé, room GC'd
  // pendant une coupure du kiosque, conteneur redémarré).
  // Ce handler MANQUAIT : l'échec de joinRoom était totalement silencieux. Comme
  // joinRoom pose roomCode de façon optimiste, le joueur voyait un lobby normal
  // mais vide, et deux joueurs qui scannaient le même QR mort se retrouvaient
  // chacun dans son lobby fantôme sans jamais se voir.
  socket.on("roomError", ({ message } = {}) => {
    const msg = message || "Room not found";
    // Spectateur : requestRoomState peut aussi émettre roomError en cours de partie.
    // On ne casse pas son état, on remonte juste le message.
    if (get().isSpectator) {
      set({ loading: false, roomError: msg });
      return;
    }
    set({ loading: false, roomCode: null, players: [], roomError: msg });
  });

  // Erreurs de connexion : enlever les loaders pour éviter blocage UI
  socket.on("connect_error", (err) => {
    console.error("Socket connect_error:", err);
    set({ loading: false });
  });

  socket.on("disconnect", (reason) => {
    // Strict: ne pas reset le store automatiquement ici (leaveRoom/resetGame gèrent ça)
    console.log("Socket disconnected:", reason);
  });
}

/*
 * createRoom / joinRoom / joinAsSpectator:
 * - Set roomCode IMMEDIATELY so UI can show lobby while waiting server ack.
 * - Attach listeners once (initSocketListeners) before socket.connect().
 * - Use socket.once timeouts to clear loading if server non-réactif (defensive).
 */
export const useGameStore = create<GameState>((set, get) => ({
  playerName: "",
  currentPosition: 0,
  currentQuestion: 0,
  score: 0,
  gameStarted: false,
  timeRemaining: 20,
  isMoving: false,
  questions: [],
  isHost: false,
  roomCode: null,
  roomError: null,
  playerLanguage: null,
  players: [],
  loading: false,
  socket: socket,
  roomHostId: null,
  isSpectator: false,
  isSynced: false,
  gameCompleted: false,
  finalPlayers: null,
  finishedPlayers: new Set(),
  playerFinished: false,
  introPhase: null,
  introStartedAt: null,
  timePerQuestion: 20,
  setIsSynced: (synced: boolean) => set({ isSynced: synced }),

  setPlayerName: (name) => set({ playerName: name }),

  setGameCompleted: (completed) => {
    const { roomCode } = get();
    if (completed && roomCode) {
      // Signaler au serveur que CE joueur a fini (pas que la game est finie)
      socket.emit("playerFinished", { roomCode });
    }
    // Marquer localement que le joueur a fini ses questions (écran d'attente)
    // gameCompleted sera mis à true par le serveur quand TOUS ont fini
    set({ playerFinished: completed });
  },

  startGame: async () => {
    const { roomCode } = get();
    if (roomCode) {
      // Le serveur génère les questions et broadcast gameStarted avec le set de questions.
      // Le handler socket "gameStarted" mettra à jour le store.
      socket.emit("startGame", { roomCode });
    }
  },

  createRoom: async (hostOnly?: boolean, questionSetId?: string, scheduleUserId?: string) => {
    try {
      set({ loading: true, roomError: null });
      const code = generateRoomCode();

      set({ roomCode: code });

      initSocketListeners(set, get);

      // Attendre que le socket soit connecté avant d'émettre
      const emitCreate = () => {
        socket.emit("createRoom", {
          code,
          playerName: hostOnly ? "" : get().playerName,
          hostOnly: !!hostOnly,
          questionSetId: questionSetId || undefined,
          scheduleUserId: scheduleUserId || undefined,
        });
      };

      if (socket.connected) {
        emitCreate();
      } else {
        socket.connect();
        socket.once("connect", emitCreate);
      }

      // Defensive timeout: if server does not respond, stop loading to avoid freeze
      const t = setTimeout(() => {
        console.warn("createRoom timeout");
        set({ loading: false });
      }, 10000);

      socket.once("roomCreated", () => {
        clearTimeout(t);
        // server handler will set final state
      });
    } catch (error) {
      console.error("Failed to create room:", error);
      set({ loading: false });
    }
  },

  // rehostRoom : réémet createRoom avec reclaim=true et le MÊME code après une
  // reconnexion socket. Indispensable pour le kiosque (/auto-room), qui affiche un
  // QR figé : sans ça le code annoncé survit à la room et tous les scans suivants
  // échouent silencieusement. Le serveur reprend la room si elle vit encore
  // (joueurs conservés), sinon la recrée avec ce code exact.
  rehostRoom: (questionSetId?: string, scheduleUserId?: string) => {
    const { roomCode } = get();
    if (!roomCode) return;
    socket.emit("createRoom", {
      code: roomCode,
      playerName: "",
      hostOnly: true,
      reclaim: true,
      questionSetId: questionSetId || undefined,
      scheduleUserId: scheduleUserId || undefined,
    });
  },

  joinRoom: async (code: string, language?: string) => {
    try {
      set({ loading: true, roomError: null, playerLanguage: language || null });

      // set roomCode immediately so UI can reflect the join attempt
      set({ roomCode: code });

      initSocketListeners(set, get);

      const emitJoin = () => {
        socket.emit("joinRoom", {
          code,
          playerName: get().playerName,
          // null = langue d'origine du set ; le serveur y retombe de lui-même.
          language: language || undefined,
        });
      };

      if (socket.connected) {
        emitJoin();
      } else {
        socket.connect();
        socket.once("connect", emitJoin);
      }

      const t = setTimeout(() => {
        console.warn("joinRoom timeout");
        set({ loading: false });
      }, 10000);

      socket.once("roomJoined", () => {
        clearTimeout(t);
      });
    } catch (error) {
      console.error("Failed to join room:", error);
      set({ loading: false });
    }
  },

  joinAsSpectator: async (code: string) => {
    try {
      set({ loading: true });

      // set roomCode immediately so spectator UI can render
      set({ roomCode: code });

      // mark as not-yet-synchronized; SpectatorView will show "synchronisation..."
      set({ isSynced: false });

      initSocketListeners(set, get);

      socket.connect();
      socket.emit("joinAsSpectator", { code });

      const t = setTimeout(() => {
        console.warn("joinAsSpectator timeout");
        set({ loading: false });
      }, 10000);

      socket.once("roomJoined", () => {
        clearTimeout(t);
        // Mark local state: this client is now a spectator and no longer host
        set({ isSpectator: true, isHost: false });

        // demande explicite de l'état actuel de la room pour garantir positions/scores à jour
        try {
          socket.emit("requestRoomState", { roomCode: code });
        } catch (err) {
          console.error("Failed to request room state:", err);
        }

        // Retry mechanism: if after 2s we are still not synced, request state again (up to 3 times)
        let attempts = 0;
        const maxAttempts = 3;
        const retryDelay = 2000;
        const retry = () => {
          attempts += 1;
          if (get().isSynced) return;
          if (attempts > maxAttempts) {
            console.warn("Spectator: requestRoomState retries exhausted");
            return;
          }
          try {
            socket.emit("requestRoomState", { roomCode: code });
            console.debug(
              `Spectator: retrying requestRoomState (attempt ${attempts})`
            );
          } catch (err) {
            console.error("Spectator: failed to re-request room state:", err);
          }
          setTimeout(retry, retryDelay);
        };
        setTimeout(retry, retryDelay);
      });
    } catch (error) {
      console.error("Failed to join as spectator:", error);
      set({ loading: false });
      throw error;
    }
  },

  leaveRoom: async () => {
    try {
      set({ loading: true });
      const { roomCode } = get();

      if (roomCode) {
        socket.emit("leaveRoom", { roomCode });
      }
      socket.removeAllListeners();
      socket.disconnect();
      socketListenersInitialized = false;

      set({
        isHost: false,
        roomCode: null,
        roomError: null,
        roomHostId: null,
        players: [],
        loading: false,
        gameStarted: false,
        questions: [],
        isSpectator: false,
        isSynced: false,
        gameCompleted: false,
        finalPlayers: null,
        playerFinished: false,
        finishedPlayers: new Set(),
        currentQuestion: 0,
        score: 0,
        currentPosition: 0,
        introPhase: null,
        introStartedAt: null,
        timePerQuestion: 20,
      });
    } catch (error) {
      console.error("Failed to leave room:", error);
      set({ loading: false });
    }
  },

  movePlayer: (steps) => {
    const { roomCode, isSpectator, gameCompleted, playerFinished } =
      get();
    // playerFinished ferme la porte dès QUE CE joueur a fini, sans attendre que
    // tout le monde ait terminé (gameCompleted). Un joueur qui attend les autres
    // ne doit plus bouger, ni en avant ni en arrière.
    if (isSpectator || gameCompleted || playerFinished) return;

    set({ isMoving: true });
    setTimeout(() => set({ isMoving: false }), 300);

    const newPosition = Math.max(0, get().currentPosition + steps);

    if (roomCode) {
      socket.emit("updatePlayer", {
        roomCode,
        position: newPosition,
        score: get().score,
      });
    }

    set((state) => ({
      currentPosition: newPosition,
      players: state.players.map((p) =>
        p.id === socket.id ? { ...p, position: newPosition } : p
      ),
    }));
  },

  setCurrentQuestion: (questionIndex) => {
    set({ currentQuestion: questionIndex });
    // Remonte la question courante au serveur pour que l'ecran spectateur puisse
    // afficher la bulle de chaque joueur. Evenement dedie plutot que greffe sur
    // updatePlayer : celui-ci part AVANT l'avancement de la question (au moment de
    // la reponse), il porterait donc toujours l'index precedent.
    const { roomCode, isSpectator } = get();
    if (roomCode && !isSpectator) {
      socket.emit("playerQuestion", { roomCode, questionIndex });
    }
  },

  updateScore: (points) => {
    const { roomCode, score, isSpectator, gameCompleted, playerFinished } = get();
    if (isSpectator || gameCompleted || playerFinished) return;

    const newScore = score + points;

    if (roomCode) {
      socket.emit("updatePlayer", {
        roomCode,
        position: get().currentPosition,
        score: newScore,
      });
    }

    set({ score: newScore });
  },

  setTimeRemaining: (time) => set({ timeRemaining: time }),
  setPlayers: (players) => set({ players }),

  setPlayerReady: async () => {
    const { roomCode } = get();
    if (!roomCode) return;

    // optimistic local update: mark current socket player as ready
    set((state) => ({
      players: state.players.map((p) =>
        p.id === socket.id ? { ...p, ready: true } : p
      ),
    }));

    try {
      socket.emit("playerReady", { roomCode });
    } catch (err) {
      console.error("Failed to emit playerReady:", err);
    }
  },

  // startSolo : sortie explicite pour le joueur seul. Le serveur n'arme plus de
  // countdown tant qu'il n'y a pas 2 joueurs, donc sans ce bouton un joueur seul
  // resterait bloqué dans le lobby.
  startSolo: () => {
    const { roomCode } = get();
    if (!roomCode) return;
    socket.emit("startSolo", { roomCode });
  },

  resetGame: () => {
    socket.removeAllListeners();
    socket.disconnect();
    socketListenersInitialized = false;
    set({
      currentPosition: 0,
      currentQuestion: 0,
      score: 0,
      timeRemaining: 20,
      isMoving: false,
      gameStarted: false,
      playerName: "",
      questions: [],
      isHost: false,
      roomCode: null,
      roomError: null,
      roomHostId: null,
      players: [],
      loading: false,
      isSpectator: false,
      isSynced: false,
      gameCompleted: false,
      finalPlayers: null,
      playerFinished: false,
      finishedPlayers: new Set(),
      introPhase: null,
      introStartedAt: null,
      timePerQuestion: 20,
    });
  },
}));
